import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Textarea } from "@chakra-ui/react";
import { Table } from "@familyzone/component-library";
import StandardModal from "js/components/shared/StandardModalV2";
import { DatePickerFilter } from "js/utils/DatePickerFilter";
import NuSingleSelect from "js/components/NuSingleSelect";
import { getUserDisplayTextForSelect } from "js/utils/userUtils";
import { submitSJRRequest } from "js/api/SJRApi";
import iconExportSuccess from "images/IconExportSuccess.svg?url";
import iconExportDocument from "images/IconExportDocument.svg?url";
import iconTimeSJR from "images/IconTimeSJR.svg?url";
import moment from "moment";
import { useUserSessionStore } from "stores/v2/userSessionStore";

const SJRModal = ({ students, handleClose }) => {
	const [currentMessage, setCurrentMessage] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [includeLastHour, setIncludeLastHour] = useState(false);
	const [hasIncludeLastHour, setHasIncludeLastHour] = useState(true);
	const [errorStudent, setErrorStudent] = useState("");
	const [errorLastHour, setErrorLastHour] = useState("");
	const [errorDateRange, setErrorDateRange] = useState("");
	const [selectedStudent, setSelectedStudent] = useState("");
	const [startDate, setStartDate] = useState(moment().startOf("day"));
	const [endDate, setEndDate] = useState(moment().endOf("day"));
	const userEmail = useUserSessionStore.getState().getTeacherEmail();

	const handleMessageChange = (evt) => {
		setCurrentMessage(evt.target.value);
	};

	const changeIncludeLastHour = (event) => {
		setIncludeLastHour(event.target.value === "yes");
		setErrorLastHour("");
	};

	const prepareAvailableUsers = () => {
		return students
			.map((student) => {
				student.fname = student.fname || "";
				student.lname = student.lname || "";
				return student;
			})
			.map((student) => ({
				text: getUserDisplayTextForSelect(student),
				value: student.username,
			}));
	};

	const handleSubmit = async () => {
		let error = false;
		setErrorDateRange("");

		if (hasIncludeLastHour && includeLastHour === null) {
			setErrorLastHour("Please select whether the last hour of activity should be included.");
			error = true;
		}

		if (selectedStudent === null || selectedStudent === "") {
			setErrorStudent("Please select a student.");
			error = true;
		}

		if (!userEmail) {
			setErrorDateRange(
				"You dont have a valid email to send the Student Journey Report. Please update your email address in school manager."
			);
			error = true;
		}

		if (error) return;

		setErrorLastHour("");
		setErrorStudent("");
		setIsSubmitting(true);

		try {
			var student = students.filter((student) => student.username === selectedStudent)[0];
			await submitSJRRequest(selectedStudent, {
				user_full_name: `${student.fname} ${student.lname}`.trim(),
				recipient_emails: [userEmail],
				device_name: useUserSessionStore.getState().deviceinfo.user_defined_name,
				user_browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				sort_ascending: false,
				sort_column: "string_datetime",
				comment: currentMessage,
				start_date: startDate.format("YYYY-MM-DD"),
				end_date: endDate.format("YYYY-MM-DD"),
				start_time: startDate.format("HH:mm:ss"),
				end_time: endDate.format("HH:mm:ss"),
				time_zone: useUserSessionStore.getState().deviceinfo.timezone,
				include_last_hour: includeLastHour,
			});
			setIsSubmitting(false);
			setHasSubmitted(true);
			setTimeout(handleClose, 5000);
		} catch (err) {
			if (err.response.status === 504) {
				setErrorDateRange("Failed to export the report, too much data in selected date range. Please try a different date range.");
			} else {
				setErrorDateRange("We couldn't find any activity in the selected date range. Please try a different date range.");
			}

			setIsSubmitting(false);
		}
	};

	const handleDateChangeCallback = (start, end) => {
		setStartDate(start);
		setEndDate(end);
		if (start.isSame(moment(), "day") || end.isSame(moment(), "day")) {
			setHasIncludeLastHour(true);
		} else {
			setHasIncludeLastHour(false);
		}
	};

	const handleSetStudent = (studentEmail) => {
		setSelectedStudent(studentEmail);
		setErrorStudent("");
	};

	return (
		<StandardModal open close={handleClose} size="medium" contentClassName="SJRModal" rootClassName="SJRModal_wrapper">
			<Table border="hidden" padding={10} paddingTop={15}>
				{hasSubmitted && (
					<div className="SJRModal_success">
						<img className="SJRModal_iconSuccess" src={hasSubmitted ? iconExportSuccess : ""} alt="" />
					</div>
				)}
				<div className="SJRModal_header" data-testid="sjr-modal">
					<Box display="flex" justifyContent="center" marginBottom={2} marginTop={3}>
						<h2>
							{hasSubmitted ? (
								<div className="SJRModal_successText">
									This Student Journey Report <br /> will be emailed to
								</div>
							) : (
								"Export Student Journey Report"
							)}
						</h2>
					</Box>
					{hasSubmitted ? (
						<div className="SJRModal_successEmail">{userEmail}</div>
					) : (
						"The report details the students' activities in your classes"
					)}
				</div>
				{hasSubmitted && (
					<Box display="flex" justifyContent="center" className="SJRModal_lastHour">
						<div className="SJRModal_lastHourText SJRModal_successText">
							{includeLastHour ? (
								<img src={iconTimeSJR} className="SJRModal_lastHourImgIcon" alt="" />
							) : (
								<i className="fa fa-info-circle SJRModal_lastHourIcon" />
							)}

							{includeLastHour
								? "Reports including the last hour may take up to an hour to process."
								: "Large data files may take up to 1 minute to process."}
						</div>
					</Box>
				)}
				{!hasSubmitted && (
					<div className="SJRModal_item">
						<NuSingleSelect
							allItems={prepareAvailableUsers()}
							searchPlaceholder="Enter Student Name"
							title="Select student"
							onChange={handleSetStudent}
						/>
						{errorStudent && <div className="error">{errorStudent}</div>}
					</div>
				)}
				<div className="SJRModal_item">
					<div className="SJRModal_itemText">Date Range</div>
					<div className="SJRModal_itemForm SJRModal_date-picker">
						<DatePickerFilter
							additionalClasses="SJRModal_date-range"
							startDate={startDate}
							endDate={endDate}
							maxDate={moment()}
							onCallback={handleDateChangeCallback}
							opens="right"
						/>
					</div>
					{errorDateRange && <div className="error">{errorDateRange}</div>}
				</div>
				{hasIncludeLastHour && (
					<div className="SJRModal_item" onChange={changeIncludeLastHour}>
						<div className="SJRModal_itemText">Include the last hour of activity</div>
						<div className="SJRModal_itemForm SJRModal_itemFormGroup">
							<div className="SJRModal_itemFormElement">
								<input type="radio" value="yes" name="lasthour" checked={includeLastHour ? "checked" : ""} readOnly />
								Yes
							</div>
							<div className="SJRModal_itemFormElement">
								<input type="radio" value="no" name="lasthour" checked={!includeLastHour ? "checked" : ""} readOnly />
								No
							</div>
						</div>

						{errorLastHour && <div className="error">{errorLastHour}</div>}

						{includeLastHour && (
							<div className="SJRModal_lastHour">
								<Box className="SJRModal_lastHourText" display="flex" alignItems="center">
									<img src={iconTimeSJR} className="SJRModal_lastHourImgIcon" alt="" />
									Reports including the last hour may take up to an hour to process.
								</Box>
							</div>
						)}
					</div>
				)}
				<div className="SJRModal_item">
					<div className="SJRModal_itemText">Cover Page Comment (optional)</div>
					<div className="SJRModal_itemForm">
						<Textarea
							sx={{ height: "134px" }}
							className="SJRModal_textArea"
							name="messageBody"
							onChange={handleMessageChange}
							value={currentMessage}
						/>
					</div>
				</div>
				<div className="SJRModal_footer">
					<button
						disabled={isSubmitting}
						onClick={handleSubmit}
						className="SJRModal_submitButton MuiButtonBase-root MuiButton-root MuiButton-text mui-flat-button mui-flat-button blue-text-button"
					>
						{isSubmitting ? (
							<span>
								<div className="SJRModal_buttonLoadingIcon"> </div>
								Sending Report
							</span>
						) : (
							<span>
								<img className="SJRModal_buttonIcon" src={iconExportDocument} alt="" />
								Export
							</span>
						)}
					</button>
					<button onClick={handleClose} className="SJRModal_cancelButton">
						Cancel
					</button>
				</div>
				{hasSubmitted && (
					<div className="SJRModal_successfooter">In case you don&apos;t get an email, check your spam folder or try again.</div>
				)}
			</Table>
		</StandardModal>
	);
};

SJRModal.propTypes = {
	handleClose: PropTypes.func.isRequired,
	students: PropTypes.array,
};

export default SJRModal;
